// Oylan stand: homework of the club sheets (oylan/spec_domashka.md).
//
// The child answers first. Two wrong answers open the teacher's method as steps she answers
// herself; a step she misses twice is shown and logged. Every task with steps comes back as a
// "twin" (same type, other numbers) after 1, 3 and 7 days; two twins right at the first try in
// a row mean the type is learned. A wrong answer is matched to the step it went wrong on, so
// the page can say "в прошлый раз ты ошиблась на этом же шаге".
//
// The journal lives in localStorage under "domashka": keys starting with "oylan-" are wiped by
// "Начать заново", and the history of mistakes must survive that.

import { todayKey } from "./session";

export const HOMEWORK_KEY = "domashka";
export const REVIEW_DAYS = [1, 3, 7];
export const LEARNED_STREAK = 2;

export function addDays(day, n) {
    const [y, m, d] = day.split("-").map(Number);
    return todayKey(new Date(y, m - 1, d + n));
}

// «Домашнее задание от 15.10 по теме «Невезучий Петя»»; the same wording as lesson_label in oylan/homework.py.
export function homeworkLabel(lesson) {
    const [, m, d] = lesson.lesson.split("-");
    const names = (lesson.topics || []).map((t) => `«${t}»`);
    if (names.length === 0) return `Домашнее задание от ${d}.${m}`;
    const which = names.length === 1 ? `по теме ${names[0]}`
        : `по темам ${names.slice(0, -1).join(", ")} и ${names[names.length - 1]}`;
    return `Домашнее задание от ${d}.${m} ${which}`;
}

// "10 000" is one number, "4 и 3" are two.
export function numbersOf(text) {
    const joined = String(text ?? "").replace(/(\d) (?=\d{3}(?!\d))/g, "$1");
    return (joined.match(/\d+/g) || []).map(Number);
}

export function keyOf(numbers) {
    return [...numbers].sort((a, b) => a - b).join(" и ");
}

export function isRight(item, input) {
    if (item.kind === "choice") return input === item.answer;
    if (item.kind === "parity_number") {
        const digits = String(input ?? "").replace(/\s/g, "").replace(/^0+(?=\d)/, ""); // 000002 is 2, one digit
        if (!/^\d+$/.test(digits) || digits.length < item.answer.min_digits) return false;
        return (Number(digits.slice(-1)) % 2 === 0) === (item.answer.parity === "чётное");
    }
    return keyOf(numbersOf(input)) === keyOf(item.answer);
}

function stepRight(step, input) {
    if (step.kind === "choice") return input === step.answer;
    const numbers = numbersOf(input);
    return numbers.length === 1 && numbers[0] === step.answer;
}

// Which step a wrong answer went wrong on, and what to say. Never names the answer.
export function diagnose(item, input) {
    if (item.kind === "parity_number") {
        const digits = String(input ?? "").replace(/\s/g, "").replace(/^0+(?=\d)/, ""); // 000002 is 2, one digit
        if (digits.length < item.answer.min_digits) return { step: 1, text: "Число маленькое. Сосчитай, сколько в нём цифр." };
        return { step: 0, text: `Посмотри на последнюю цифру. Число должно быть ${item.answer.parity}.` };
    }
    if (item.kind === "numbers") {
        const numbers = numbersOf(input);
        const known = item.mistakes[keyOf(numbers)];
        if (known) return known;
        if (numbers.length !== item.answer.length) {
            return { step: null, text: item.answer.length === 1 ? "В ответе должно быть одно число." : `В ответе должно быть ${item.answer.length} числа.` };
        }
        // An answer of an earlier step: she stopped too early.
        const finalSteps = item.steps.length - item.answer.length;
        for (let i = 0; i < finalSteps; i++) {
            if (numbers.includes(item.steps[i].answer) && !item.answer.includes(item.steps[i].answer)) {
                return { step: i + 1, text: "Это число из середины решения, а не ответ. Сделай следующий шаг." };
            }
        }
    }
    return { step: null, text: `Проверь ответ по условию. Правило: ${item.rule}` };
}

export function newEntry() {
    return { status: "new", tries: [], wrong: 0, stepsOn: null, stepAt: 0, stepWrong: 0, shown: [], errors: [], review: null, streak: 0 };
}

function itemOf(task, twin) {
    return twin === null || twin === undefined ? task : task.twins[twin % task.twins.length];
}

// What the page shows now: the task itself, its steps, a review twin, or nothing to do today.
export function current(task, entry = newEntry(), today = todayKey()) {
    switch (entry.status) {
        case "new":
            return { mode: task.kind === "steps" ? "steps" : "own", item: task, twin: null };
        case "steps":
            return { mode: "steps", item: itemOf(task, entry.stepsOn), twin: entry.stepsOn };
        case "review":
            if (entry.review.due > today) return { mode: "wait", item: null, twin: null, due: entry.review.due };
            return { mode: "review", item: itemOf(task, entry.review.twin), twin: entry.review.twin };
        default:
            return { mode: entry.status, item: null, twin: null };
    }
}

function repeated(entry, step, today) {
    return step !== null && step !== undefined && entry.errors.some((e) => e.step === step && e.day < today);
}

function remember(text, item, entry, step, today) {
    if (!repeated(entry, step, today)) return text;
    const q = item.steps[step]?.q || "";
    return `${text} В прошлый раз ты ошиблась на этом же шаге: «${q}»`;
}

function toReview(entry, nextTwin, today) {
    return { ...entry, status: "review", review: { due: addDays(today, REVIEW_DAYS[0]), stage: 0, twin: nextTwin }, wrong: 0, streak: 0 };
}

// The child's answer to the task or to a review twin.
export function submit(task, entry = newEntry(), input, today = todayKey()) {
    const { mode, item, twin } = current(task, entry, today);
    if (mode !== "own" && mode !== "review") return { entry, ok: false, diagnosis: null };
    const ok = isRight(item, input);
    let next = { ...entry, tries: [...entry.tries, { day: today, input: String(input), ok, twin }] };
    if (ok) {
        if (mode === "own" && item.kind === "choice" && entry.wrong === 0) {
            // A yes/no can be guessed: one twin must confirm it before the task counts as learned.
            return { entry: { ...toReview(next, 0, today), streak: 1 }, ok, diagnosis: null };
        }
        if (mode === "own") return { entry: { ...next, status: "done", wrong: 0 }, ok, diagnosis: null };
        const first = entry.wrong === 0;
        const stage = first ? entry.review.stage + 1 : 0;
        const streak = first ? entry.streak + 1 : 0;
        if (streak >= LEARNED_STREAK) return { entry: { ...next, status: "learned", wrong: 0, streak }, ok, diagnosis: null };
        const due = addDays(today, REVIEW_DAYS[Math.min(stage, REVIEW_DAYS.length - 1)]);
        return { entry: { ...next, wrong: 0, streak, review: { due, stage, twin: twin + 1 } }, ok, diagnosis: null };
    }
    const found = diagnose(item, input);
    const diagnosis = { ...found, text: remember(found.text, item, entry, found.step, today) };
    next = { ...next, wrong: entry.wrong + 1, streak: 0 };
    if (found.step !== null && found.step !== undefined) next.errors = [...entry.errors, { day: today, twin, step: found.step }];
    // With two choices a second guess is always right, so a choice task goes to the steps at once.
    if (next.wrong >= (item.kind === "choice" ? 1 : 2)) next = { ...next, status: "steps", stepsOn: twin, stepAt: 0, stepWrong: 0 };
    return { entry: next, ok, diagnosis };
}

// The child's answer to one step of the method.
export function submitStep(task, entry, input, today = todayKey()) {
    const { mode, item, twin } = current(task, entry, today);
    if (mode !== "steps") return { entry, ok: false };
    const s = item.steps[entry.stepAt];
    const ok = stepRight(s, input);
    let next = { ...entry };
    let shown = null;
    let note = null;
    if (ok) {
        next = { ...next, stepAt: entry.stepAt + 1, stepWrong: 0 };
    } else {
        if (repeated(entry, entry.stepAt, today)) note = "В прошлый раз ты ошиблась на этом же шаге.";
        next = { ...next, stepWrong: entry.stepWrong + 1, errors: [...entry.errors, { day: today, twin, step: entry.stepAt }] };
        if (next.stepWrong >= 2) {
            shown = { explain: s.explain, answer: s.answer };
            next = { ...next, shown: [...entry.shown, { day: today, twin, step: entry.stepAt }], stepAt: entry.stepAt + 1, stepWrong: 0 };
        }
    }
    const finished = next.stepAt >= item.steps.length;
    if (finished) {
        const nextTwin = twin === null || twin === undefined ? 0 : twin + 1;
        const alone = task.kind === "steps" && (twin === null || twin === undefined)
            && !next.errors.some((e) => e.day === today && (e.twin === null || e.twin === undefined));
        next = alone ? { ...next, status: "done" } : toReview(next, nextTwin, today);
    }
    return { entry: next, ok, shown, note, finished };
}

// For the parent: on which steps the child goes wrong, and how often.
export function summary(task, entry = newEntry()) {
    const byStep = {};
    for (const e of entry.errors) {
        const q = itemOf(task, e.twin).steps[e.step]?.q || `шаг ${e.step + 1}`;
        byStep[e.step] = byStep[e.step] || { step: e.step, q, times: 0 };
        byStep[e.step].times += 1;
    }
    return {
        status: entry.status,
        tries: entry.tries.length,
        firstOwn: entry.tries.find((t) => t.twin === null || t.twin === undefined) || null,
        shown: entry.shown.length,
        weakSteps: Object.values(byStep).filter((s) => s.times >= 2).sort((a, b) => b.times - a.times),
        due: entry.review?.due || null,
    };
}

export function dueToday(homework, journal, today = todayKey()) {
    return homework.flatMap((l) => l.tasks).filter((t) => current(t, journal[t.id], today).mode === "review").length;
}

export function loadJournal(storage = window.localStorage) {
    try {
        return JSON.parse(storage.getItem(HOMEWORK_KEY)) || {};
    } catch (e) {
        return {};
    }
}

export function saveJournal(journal, storage = window.localStorage) {
    try {
        storage.setItem(HOMEWORK_KEY, JSON.stringify(journal));
    } catch (e) {
        // A private window without storage: the page still works, the journal is not kept.
    }
}
